import React, { useEffect, useState } from "react";
import { Divider } from "antd";
import Typography from "../../../../components/Typography";
import SectionItem from "../../../../components/SectionItem";
import { MainContainer, Section } from "./styles";
import useEnterprise from "../../hooks/useEnterprise";
import { EditorialTree as EditorialTreeType } from "./types";
import { editorialTreeService } from "../../../../services/modules/editorialTree";
import WaitingMessage from "../../../../components/WaitingMessage";
import { SkeletonLoading } from "../skeleton";

const EditorialTree: React.FC = (): JSX.Element => {
  const { enterpriseState } = useEnterprise();

  const [editorialTreeData, setEditorialTreeData] =
    useState<EditorialTreeType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchEditorialTree = (id: string) => {
    editorialTreeService
      .getEditorialTree(id)
      .then((response) => setEditorialTreeData(response))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (enterpriseState.selectedEnterprise)
      fetchEditorialTree(enterpriseState.selectedEnterprise);
  }, [enterpriseState.selectedEnterprise]);

  return (
    <Section>
      {isLoading ? (
        <SkeletonLoading />
      ) : (
        <>
          {editorialTreeData ? (
            <MainContainer>
              <Typography
                text={enterpriseState.enterpriseData?.company_name}
                size="extraHuge"
                color="Black500"
                weight={700}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"PROBLEMA"}
                description={
                  "Problemas que a persona enfrenta ou enfrentará ao longo da sua vida."
                }
                isTitle
              />
              {editorialTreeData.problems.map((problem) => (
                <SectionItem
                  icon={"🔴"}
                  title={`Nível ${problem.level}`}
                  description={problem.list}
                  isList
                />
              ))}
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"SOLUÇÕES"}
                description={
                  "Como o produto, serviço ou marca resolvem os problemas acima de forma abrangente ou específica."
                }
                isTitle
              />
              {editorialTreeData.solutions.map((solution, index) => (
                <SectionItem
                  key={index}
                  icon={"💡"}
                  title={`Solução ${index + 1}`}
                  description={solution}
                />
              ))}
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"NÍVEIS DE CONSCIÊNCIA"}
                description={
                  "Os níveis de consciência descrevem o estágio de percepção e entendimento de um consumidor em relação a um problema, solução ou marca."
                }
                isTitle
              />
              <SectionItem
                icon={"🔍"}
                title={"Totalmente Inconsciente"}
                isList
                description={
                  editorialTreeData.consciousness_level.totally_unconscious ||
                  []
                }
              />
              <SectionItem
                icon={"⚠️"}
                title={"Consciente do Problema"}
                isList
                description={
                  editorialTreeData.consciousness_level.problem_consciousness ||
                  []
                }
              />
              <SectionItem
                icon={"✅"}
                title={"Consciente da Solução"}
                isList
                description={
                  editorialTreeData.consciousness_level
                    .solution_consciousness || []
                }
              />
              <SectionItem
                icon={"📦"}
                title={"Consciente do Produto"}
                isList
                description={
                  editorialTreeData.consciousness_level.product_consciousness ||
                  []
                }
              />
              <SectionItem
                icon={"🏷️"}
                title={"Consciente da Marca"}
                isList
                description={
                  editorialTreeData.consciousness_level.brand_consciousness ||
                  []
                }
              />
              <SectionItem
                icon={"🌟"}
                title={"Extremamente Consciente"}
                isList
                description={
                  editorialTreeData.consciousness_level
                    .extremely_consciousness || []
                }
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"TEMAS E ASSUNTOS"}
                description={
                  "Principais áreas temáticas que a marca irá abordar e lista de tópicos para cobrir em cada categoria."
                }
                isTitle
              />
              <SectionItem
                icon={"📚"}
                title={"Categorias e Subcategorias de Conteúdo"}
                isList
                description={
                  editorialTreeData.themes_and_subjects.content_categories.map(
                    (theme) => theme.title
                  ) || []
                }
              />
              <SectionItem
                icon={"📌"}
                title={"Tópicos Especícos"}
                isList
                description={
                  editorialTreeData.themes_and_subjects.specific_topics || []
                }
              />
              <SectionItem
                icon={"🔑"}
                title={"Palavras-chave"}
                isList
                description={
                  editorialTreeData.themes_and_subjects.key_words || []
                }
              />
            </MainContainer>
          ) : (
            <WaitingMessage />
          )}
        </>
      )}
    </Section>
  );
};

export default EditorialTree;
